import React, { useEffect } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  Heading,
  Page,
  LayoutSingleColumn,
  IconSearch,
  Icons,
  StrapiContentRenderer,
  IconSpinner,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SupportDetailPage.module.css';
import {
  fetchStrapiHelpPages,
  getHelpPages,
  getHelpPagesInProgress,
} from '../../ducks/strapi.duck';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { findMatchingObject } from '../SupportListPage/SupportListPage';
import { isArrayLength } from '../../util/genericHelpers';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { findHelpArticle } from '../../util/helpArticles';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

const useHelpPages = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStrapiHelpPages());
  }, [dispatch]);

  const helpPagesContent = useSelector(getHelpPages);
  const searchInProgress = useSelector(getHelpPagesInProgress);

  return { helpPagesContent, searchInProgress };
};

const SupportDetailPageComponent = ({
  history,
  routeConfiguration,
  marketplaceName,
  isKeywordSearch,
  intl,
  scrollingDisabled,
  params: { slug, id, title },
  staticContext,
}) => {
  const { helpPagesContent: supportListPagesContent, searchInProgress } = useHelpPages();

  const currentSupportListPageContent = supportListPagesContent
    ? findMatchingObject(supportListPagesContent, slug)
    : null;

  const articles = currentSupportListPageContent?.attributes?.content || [];
  const currentArticle = findHelpArticle(articles, { id, title });

  const navigateToDetailPage = (page, params) => {
    history.push(createResourceLocatorString(page, routeConfiguration, { ...params }, {}));
  };

  if (supportListPagesContent && !searchInProgress && !currentArticle) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  return (
    <Page scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageRoot}>
          {searchInProgress ? (
            <div className={css.searchInProgress}>
              <IconSpinner />
            </div>
          ) : (
            <div className={css.introContent}>
              <div className={css.breadcrumbs}>
                <span role="button" onClick={() => navigateToDetailPage('SupportPage', {})}>
                  {intl.formatMessage({ id: 'SupportDetailPage.title' })}
                </span>
                <Icons name="rightAngle" />
                <span
                  role="button"
                  onClick={() => navigateToDetailPage('SupportListPage', { type: slug })}
                >
                  {currentSupportListPageContent?.attributes?.title}
                </span>
                <Icons name="rightAngle" />
                <span className={css.title}>{currentArticle?.title}</span>
              </div>
              <div className={css.introInnerBlock}>
                <h1>{currentArticle?.title}</h1>
                {currentArticle?.description ? (
                  <StrapiContentRenderer content={currentArticle?.description} />
                ) : (
                  ''
                )}
              </div>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SupportDetailPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  marketplaceName: string.isRequired,
  isKeywordSearch: bool.isRequired,
  staticContext: object,
  intl: intlShape.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const EnhancedSupportDetailPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const params = useParams();

  return (
    <SupportDetailPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      params={params}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SupportDetailPage = compose(connect(mapStateToProps))(EnhancedSupportDetailPage);

export default SupportDetailPage;
